import React from 'react';
import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import { decodeToken } from 'react-jwt';
import { message } from 'antd';
import dayjs from 'dayjs';

import mainStore from 'src/stores/mainStore';
import i18n from 'src/i18n';
import { formatDayjsWithWeekday } from 'src/utils';
import EDMService from 'src/services/edm';
import ResultService from 'src/services/result';
import { SOURCE_CATEGORY_TYPE } from 'src/constants/normal';

// > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
// ? 因為這邊 paging 的方式是固定 頁數 * 每頁筆數, 所以：
// ? 如果第一次撈完去重之後，剩下的筆數是 37, 要再去撈下 50筆
// ? 等到後面 50 筆也處理完之後，假設現在有 83 筆，一樣只顯示 50 筆
// ? 但把多的 33 筆保留在 remainingTopicList 裡面。
// ? 撈下一頁的時候，不用考慮這些目前沒有用到的，繼續往後面撈，然後再處理。
// ? 直到下一次超過50筆時，再把 新的 50 推到 topicList 裡面。
// ? 剩下的一樣再推回 remainingTopicList 裡面。
// ! 就可以避免因為志維的 anchor 是沒有 id 的關係，導致在撈下一頁的時候，會撈到重複的話題。

export default class EmailTopicPreviewPageViewModel {
  ref = React.createRef();
  newsletterId = null;
  token = null;
  @observable newsletterSubject = '主題電子報主旨';

  subjectId = null;
  @observable subjectName = '主題話題';
  @observable subjectHasEvaluation = false;

  @observable startDate = null;
  @observable endDate = null;

  @observable topicList = [];
  // > 新增剩下的 topics list 來保留超過 limit 的筆數, 下次再撈下一頁的時候, 可以繼續使用這些目前沒有在畫面上的。
  @observable remainingTopicList = [];
  searchId = null;
  @observable hasNextPage = true;

  // > 用來過濾重複話題的
  @observable urlSet = new Set();

  positionY = 0;
  @observable isShowHeader = true;

  @observable tagPool = [];

  @observable minWidth = 600;

  @observable currentPage = 1;

  @computed get ratingTags() {
    const result = {};
    this.tagPool.filter((tag) => tag.type === 'rating').forEach((tag) => {
      result[`${tag.value}`] = tag.label;
    });
    return result;
  }

  @computed get topicTags() {
    const result = {};
    this.tagPool.filter((tag) => tag.type === 'topic').forEach((tag) => {
      result[`${tag.value}`] = tag.label;
    });
    return result;
  }

  @computed get suggestionTags() {
    const result = {};
    this.tagPool.filter((tag) => tag.type === 'suggestion').forEach((tag) => {
      result[`${tag.value}`] = tag.label;
    });
    return result;
  }

  @computed get departmentTags() {
    const result = {};
    this.tagPool.filter((tag) => tag.type === 'department').forEach((tag) => {
      result[`${tag.value}`] = tag.label;
    });
    return result;
  }

  @computed get dateString() {
    if (dayjs(this.endDate).diff(this.startDate, 'day') > 1) {
      return `${formatDayjsWithWeekday(this.startDate)} - ${formatDayjsWithWeekday(this.endDate)}`;
    }
    return formatDayjsWithWeekday(this.startDate);
  }

  constructor() {
    makeObservable(this);
  }

  @action didMount = async ({ token }) => {
    if (!token) {
      return;
    }
    this.updateContent(token);

    const targetElement = this.ref.current;
    if (targetElement) {
      targetElement.addEventListener('scroll', this.scrollHandler);
    }

    const content = document.querySelector('#app').getBoundingClientRect();
    this.minWidth = Math.max(content.width, content.height);

    await Promise.all([
      this.getNewsletterDetail(),
      this.processTopics()
    ]);
  };

  @action willUnmount = () => {
    const targetElement = this.ref.current;
    if (targetElement) {
      targetElement.removeEventListener('scroll', this.scrollHandler);
    }
  };

  @action scrollHandler = () => {
    const { y } = document.querySelector('.scrollContainer').getBoundingClientRect();

    if (Math.abs(y - this.positionY) > 20) {
      // > scrolling top
      if (this.positionY < y) {
        this.isShowHeader = true;
      }
      if (this.positionY > y) {
        this.isShowHeader = false;
      }
      this.positionY = y;
    }

  };

  @action updateContent = (token) => {
    const { subject, title, hasEvaluation, showBrand, startDate, endDate } = decodeToken(token);
    console.log('results', decodeToken(token));
    this.token = token;

    this.newsletterSubject = subject;
    this.subjectName = title;
    this.subjectHasEvaluation = hasEvaluation;
    this.subjectNeedShowBrand = showBrand;
    this.startDate = startDate;
    this.endDate = endDate;
  };

  @action getNewsletterDetail = async () => {
    try {
      if (mainStore.checkIsInQueue('newsletterDetail')) {
        return;
      }
      mainStore.setLoading('newsletterDetail');
      const { tags } = await EDMService.getEDMDetailByToken(this.token);

      runInAction(() => {
        this.tagPool = tags.map((tag) => ({ value: tag.id, label: tag.value, type: tag.type }));
      });

    } catch (error) {
      message.error(i18n.t('api_get_edm_detail_error'));
    } finally {
      mainStore.setLoadingComplete('newsletterDetail');
    }
  };

  // > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
  @action processTopics = async () => {
    if (this.remainingTopicList.length > 20) {
      this.topicList = [...this.topicList, ...this.remainingTopicList.slice(0, 20)];
      this.remainingTopicList = this.remainingTopicList.slice(20);
      return;
    }

    if (this.hasNextPage) {
      try {
        if (this.currentPage === 1) {
          await this.fetchPosts();
        } else {
          await this.pagingPosts();
        }

      } catch (error) {
        message.error(i18n.t('api_get_topics_error'));
      }
    }

    if (this.remainingTopicList.length < 20 && this.hasNextPage) {
      await this.processTopics();
    } else {
      runInAction(() => {
        this.topicList = [...this.topicList, ...this.remainingTopicList.slice(0, 20)];
        this.remainingTopicList = this.remainingTopicList.slice(20);
      });
    }
  };

  // > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
  @action fetchPosts = async () => {
    try {
      if (mainStore.checkIsInQueue('topics')) {
        return;
      }
      mainStore.setLoading('topics');
      const { list, searchId } = await ResultService.getTopicsByToken(this.token);

      // > 因為要過濾話題 size 改成先撈取 50 筆
      runInAction(() => {
        this.searchId = searchId;
        this.hasNextPage = list.length === 50;
        this.currentPage += 1;
      });

      this.filterPosts(list);
    } catch (error) {
      message.error(i18n.t('api_get_topics_error'));
      this.hasNextPage = false;
    } finally {
      mainStore.setLoadingComplete('topics');
    }
  };

  // > 2025/04/24 updated -> 要篩掉 news, forum, threads, ig 的重複 url  話題
  @action pagingPosts = async () => {
    try {
      if (mainStore.checkIsInQueue('topics')) {
        return;
      }
      mainStore.setLoading('topics');
      // > 因為要過濾話題 size 改成先撈取 50 筆
      const { list } = await ResultService.getTopicsPagingByToken(this.token, { page: this.currentPage, searchId: this.searchId, size: 50 });

      // > 因為要過濾話題 size 改成先撈取 50 筆
      runInAction(() => {
        this.hasNextPage = list.length === 50;
        this.currentPage += 1;
      });

      this.filterPosts(list);

    } catch (error) {
      message.error(i18n.t('api_get_topics_error'));
      this.hasNextPage = false;
    } finally {
      mainStore.setLoadingComplete('topics');
    }
  };

  @action filterPosts = (posts) => {
    posts.forEach((post) => {
      if ([SOURCE_CATEGORY_TYPE.News, SOURCE_CATEGORY_TYPE.Forum, SOURCE_CATEGORY_TYPE.Threads, SOURCE_CATEGORY_TYPE.IG].includes(post.category)) {
        const url = post.url;
        if (this.urlSet.has(url)) {
          return;
        }
        this.urlSet.add(url);
        this.remainingTopicList.push(post);
      } else {
        this.remainingTopicList.push(post);
      }
    });
  };
}
